import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'react-hot-toast';
import { Save, Loader2, ArrowLeft, DollarSign, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get('customer');
  const preselectedItem = searchParams.get('item');
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [openItems, setOpenItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});

  const [header, setHeader] = useState({
    customer_id: preselectedCustomer || '',
    payment_method: 'BANK_TRANSFER',
    payment_date: new Date().toISOString().split('T')[0],
    value_date: new Date().toISOString().split('T')[0],
    payment_amount: '',
    bank_account: '',
    bank_reference: '',
    payment_note: ''
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    if (header.customer_id) {
      loadOpenItems(header.customer_id);
    } else {
      setOpenItems([]);
      setSelectedItems({});
    }
  }, [header.customer_id]);

  useEffect(() => {
    // Pre-select item if specified in URL
    if (preselectedItem && openItems.length > 0) {
      const item = openItems.find(i => i.item_id === preselectedItem);
      if (item) {
        setSelectedItems({
          [item.item_id]: {
            selected: true,
            allocated_amount: item.open_amount
          }
        });
        setHeader(prev => ({
          ...prev,
          payment_amount: item.open_amount.toString()
        }));
      }
    }
  }, [preselectedItem, openItems]);

  const loadCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      setCustomers(response.data || []);
    } catch (error) {
      console.error('Error loading customers:', error);
      toast.error('Failed to load customers');
    }
  };

  const loadOpenItems = async (customerId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/ar/open-items?customer_id=${customerId}&status=OPEN`);
      // Also get partially cleared items
      const partialRes = await axios.get(`${BACKEND_URL}/api/ar/open-items?customer_id=${customerId}&status=PARTIALLY_CLEARED`);
      const allItems = [...(response.data || []), ...(partialRes.data || [])];
      setOpenItems(allItems);
      
      // Initialize selected items
      const initial = {};
      allItems.forEach(item => {
        initial[item.item_id] = {
          selected: false,
          allocated_amount: 0
        };
      });
      setSelectedItems(initial);
    } catch (error) {
      console.error('Error loading open items:', error);
      toast.error('Failed to load open items');
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemToggle = (itemId) => {
    const item = openItems.find(i => i.item_id === itemId);
    if (!item) return;

    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        selected: !prev[itemId]?.selected,
        allocated_amount: !prev[itemId]?.selected ? item.open_amount : 0
      }
    }));
  };

  const handleAllocationChange = (itemId, amount) => {
    const item = openItems.find(i => i.item_id === itemId);
    if (!item) return;
    
    const maxAmount = item.open_amount;
    const validAmount = Math.min(Math.max(0, parseFloat(amount) || 0), maxAmount);
    
    setSelectedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        allocated_amount: validAmount
      }
    }));
  };

  const calculateTotalAllocated = () => {
    return Object.values(selectedItems)
      .filter(item => item.selected)
      .reduce((sum, item) => sum + (item.allocated_amount || 0), 0);
  };

  const autoAllocate = () => {
    const paymentAmount = parseFloat(header.payment_amount) || 0;
    if (paymentAmount <= 0) {
      toast.error('Enter payment amount first');
      return;
    }

    let remaining = paymentAmount;
    const newSelected = {};

    // Sort by due date (oldest first)
    const sortedItems = [...openItems].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

    for (const item of sortedItems) {
      if (remaining <= 0) break;
      
      const allocAmount = Math.min(remaining, item.open_amount);
      newSelected[item.item_id] = {
        selected: allocAmount > 0,
        allocated_amount: allocAmount
      };
      remaining -= allocAmount;
    }

    // Set remaining items to 0
    openItems.forEach(item => {
      if (!newSelected[item.item_id]) {
        newSelected[item.item_id] = { selected: false, allocated_amount: 0 };
      }
    });

    setSelectedItems(newSelected);
    toast.success('Payment auto-allocated to oldest items first');
  };

  const validateForm = () => {
    if (!header.customer_id) {
      toast.error('Please select a customer');
      return false;
    }
    if (!header.payment_method) {
      toast.error('Please select a payment method');
      return false;
    }
    if (!header.payment_date) {
      toast.error('Payment date is required');
      return false;
    }
    const paymentAmount = parseFloat(header.payment_amount);
    if (!paymentAmount || paymentAmount <= 0) {
      toast.error('Please enter a valid payment amount');
      return false;
    }
    
    const totalAllocated = calculateTotalAllocated();
    if (totalAllocated === 0) {
      toast.error('Please allocate the payment to at least one open item');
      return false;
    }
    if (totalAllocated > paymentAmount) {
      toast.error(`Total allocation (${totalAllocated.toFixed(2)}) exceeds payment amount (${paymentAmount.toFixed(2)})`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      // Build allocations
      const allocations = Object.entries(selectedItems)
        .filter(([_, data]) => data.selected && data.allocated_amount > 0)
        .map(([itemId, data]) => ({
          open_item_id: itemId,
          allocated_amount: data.allocated_amount
        }));

      const paymentData = {
        customer_id: header.customer_id,
        payment_method: header.payment_method,
        payment_date: header.payment_date,
        value_date: header.value_date || header.payment_date,
        payment_amount: parseFloat(header.payment_amount),
        bank_account: header.bank_account || null,
        bank_reference: header.bank_reference || null,
        payment_note: header.payment_note || null,
        allocations: allocations
      };

      const response = await axios.post(`${BACKEND_URL}/api/ar/payments`, paymentData);
      toast.success(`Payment '${response.data.payment_number}' created successfully!`);
      navigate(`/ar/payments/${response.data.payment_number}`);
    } catch (error) {
      console.error('Error creating payment:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to create payment: ${errorMsg}`);
    } finally {
      setIsSaving(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const totalAllocated = calculateTotalAllocated();
  const paymentAmount = parseFloat(header.payment_amount) || 0;
  const unallocated = paymentAmount - totalAllocated;

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/ar/open-items')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Post Incoming Payment</h1>
              <p className="text-sm text-gray-600">Record customer payment and clear open items</p>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={isSaving} data-testid="save-payment-btn">
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Create Payment
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Header */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Payment Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Customer *</Label>
                  <Select 
                    value={header.customer_id || '__SELECT__'} 
                    onValueChange={(v) => setHeader({...header, customer_id: v === '__SELECT__' ? '' : v})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select Customer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__SELECT__">Select Customer</SelectItem>
                      {customers.map(c => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>
                          {c.customer_id} - {c.customer_name || c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Method *</Label>
                  <Select value={header.payment_method} onValueChange={(v) => setHeader({...header, payment_method: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BANK_TRANSFER">Bank Transfer</SelectItem>
                      <SelectItem value="CHECK">Check</SelectItem>
                      <SelectItem value="CASH">Cash</SelectItem>
                      <SelectItem value="CREDIT_CARD">Credit Card</SelectItem>
                      <SelectItem value="DIRECT_DEBIT">Direct Debit</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Payment Date *</Label>
                  <Input
                    type="date"
                    value={header.payment_date}
                    onChange={(e) => setHeader({...header, payment_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Value Date</Label>
                  <Input
                    type="date"
                    value={header.value_date}
                    onChange={(e) => setHeader({...header, value_date: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Payment Amount (USD) *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={header.payment_amount}
                    onChange={(e) => setHeader({...header, payment_amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label>Bank Reference / Check #</Label>
                  <Input
                    value={header.bank_reference}
                    onChange={(e) => setHeader({...header, bank_reference: e.target.value})}
                    placeholder="Reference number"
                    maxLength={50}
                  />
                </div>
                <div className="col-span-2">
                  <Label>Payment Note</Label>
                  <Input
                    value={header.payment_note}
                    onChange={(e) => setHeader({...header, payment_note: e.target.value})}
                    placeholder="Additional notes..."
                    maxLength={500}
                  />
                </div>
              </div>
            </Card>

            {/* Open Items Allocation */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Allocate to Open Items
                </h3>
                <Button variant="outline" size="sm" onClick={autoAllocate} disabled={!header.payment_amount}>
                  Auto-Allocate
                </Button>
              </div>
              
              {!header.customer_id ? (
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">Select a customer to see open items</p>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
                  <span className="ml-3 text-gray-600">Loading open items...</span>
                </div>
              ) : openItems.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-4" />
                  <p className="text-gray-500">No open items for this customer</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 w-12"></th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Invoice</th>
                        <th className="text-left py-2 px-3 text-sm font-semibold">Due Date</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Open Amount</th>
                        <th className="text-right py-2 px-3 text-sm font-semibold">Allocation</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openItems.map((item) => {
                        const itemState = selectedItems[item.item_id];
                        const isOverdue = new Date(item.due_date) < new Date();
                        
                        return (
                          <tr key={item.item_id} className={`border-b hover:bg-gray-50 ${isOverdue ? 'bg-red-50' : ''}`}>
                            <td className="py-2 px-3">
                              <Checkbox
                                checked={itemState?.selected || false}
                                onCheckedChange={() => handleItemToggle(item.item_id)}
                              />
                            </td>
                            <td className="py-2 px-3">
                              <span className="font-mono">{item.billing_number}</span>
                            </td>
                            <td className="py-2 px-3">
                              <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                                {item.due_date?.split('T')[0]}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right font-mono">
                              {item.currency} {item.open_amount?.toFixed(2)}
                            </td>
                            <td className="py-2 px-3 text-right w-32">
                              {itemState?.selected && (
                                <Input
                                  type="number"
                                  min="0"
                                  max={item.open_amount}
                                  step="0.01"
                                  value={itemState?.allocated_amount || 0}
                                  onChange={(e) => handleAllocationChange(item.item_id, e.target.value)}
                                  className="w-28 text-right"
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Customer Info */}
            {header.customer_id && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Customer</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Customer ID</p>
                    <p className="font-mono font-bold">{header.customer_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-medium">{getCustomerName(header.customer_id)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Open</p>
                    <p className="font-mono font-bold text-red-600">
                      USD {openItems.reduce((sum, item) => sum + item.open_amount, 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </Card>
            )}

            {/* Payment Summary */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Payment Summary</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Amount:</span>
                  <span className="font-mono font-bold">USD {paymentAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Allocated:</span>
                  <span className="font-mono">USD {totalAllocated.toFixed(2)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t">
                  <span className="font-semibold">Unallocated:</span>
                  <span className={`font-mono font-bold ${unallocated > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    USD {unallocated.toFixed(2)}
                  </span>
                </div>
                {unallocated > 0 && (
                  <p className="text-xs text-orange-600">
                    Unallocated amount will be posted as "On Account"
                  </p>
                )}
              </div>
            </Card>

            {/* Help */}
            <Card className="p-6 bg-orange-50">
              <h3 className="text-sm font-semibold text-orange-800 mb-2">Payment Process</h3>
              <ul className="text-xs text-orange-700 space-y-1">
                <li>1. Select customer and enter payment amount</li>
                <li>2. Select open items to clear</li>
                <li>3. Adjust allocation amounts if needed</li>
                <li>4. Click "Create Payment" to save</li>
                <li>5. Post the payment to clear items</li>
              </ul>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PaymentCreate;
